import { storeClient } from './client';

export interface MarketplaceDetail {
  id: string;
  identifier: string;
  name: string;
  url: string;
  logo?: string;
  country?: string;
  language?: string;
  active?: boolean;
  created?: string;
  updated?: string;
}

// In-memory cache for marketplace list
let marketplaceCache: MarketplaceDetail[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch marketplace list from store service with caching
 */
export const fetchMarketplaces = async (): Promise<MarketplaceDetail[]> => {
  const now = Date.now();

  // Return cached data if still valid
  if (marketplaceCache && now - cacheTimestamp < CACHE_DURATION) {
    return marketplaceCache;
  }

  try {
    const response = await storeClient.get('/api/v1/store/marketplaces/list');
    marketplaceCache = response.data;
    cacheTimestamp = now;
    return marketplaceCache || [];
  } catch (error) {
    console.error('Failed to fetch marketplaces:', error);
    // Return cached data even if expired, or empty array if no cache
    return marketplaceCache || [];
  }
};

/**
 * Clear the marketplace cache (useful for testing or manual refresh)
 */
export const clearMarketplaceCache = (): void => {
  marketplaceCache = null;
  cacheTimestamp = 0;
};

/**
 * Resolve a BrandTrax marketplace identifier from a seller URL.
 * Uses the cached marketplace list, matching by hostname (and subdomain variants).
 */
export const resolveMarketplaceIdentifier = async (sellerUrl: string): Promise<string | null> => {
  if (!sellerUrl) return null;
  try {
    const list = await fetchMarketplaces();
    const url = new URL(sellerUrl, 'https://placeholder.tld'); // base for relative URLs
    let host = url.hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);

    // Try direct URL prefix match
    for (const mp of list) {
      const u = mp.url ? new URL(mp.url) : null;
      if (u) {
        let mpHost = u.hostname.toLowerCase();
        if (mpHost.startsWith('www.')) mpHost = mpHost.slice(4);
        if (host.endsWith(mpHost)) return mp.identifier || mp.id;
      }
    }

    // Fallback: compare by stripping first label (www) and matching registrable
    const base = host.split('.').slice(-2).join('.');
    for (const mp of list) {
      const u = mp.url ? new URL(mp.url) : null;
      if (!u) continue;
      let mpHost = u.hostname.toLowerCase();
      if (mpHost.startsWith('www.')) mpHost = mpHost.slice(4);
      const mpBase = mpHost.split('.').slice(-2).join('.');
      if (base === mpBase) return mp.identifier || mp.id;
    }
  } catch {
    // ignore
  }
  return null;
};
